/**
 * Verkaufsargument „Nach der Bestellung nicht allein“ (Wunsch Inhaber 27.10.2026):
 * echte Screenshots aus dem Kundenkonto (Produktionsstatus mit Werkstatt-Szene, Versand mit Sendungsnummer).
 * Eingebaut auf der Startseite und den Produktseiten.
 */
import React from "react";
import { m as motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Factory, Mail, Truck, FileText, ArrowRight } from "lucide-react";
import { imgSet } from "@/lib/img";
import { useLang } from "@/locale/LocaleContext";

const ICONS = [Factory, Mail, Truck, FileText];
/** Texte je Sprache (deutsche Seiten ohne Provider = de). */
const TXT = {
  de: {
    badge: "Nach der Bestellung", title: ["Sie sind nach dem Kauf ", "nicht allein."],
    text: "Transparenz ist uns wichtig. Deshalb zeigen wir Ihnen offen, wie weit Ihre Bestellung ist: von der Produktion über den Technik-Einbau und die Endkontrolle bis zur Spedition vor Ihrer Tür. Alles in Ihrem Kundenkonto, auf dem Handy und am PC.",
    points: [
      ["Produktion live verfolgen", "Zuschnitt, Montage, Pulverbeschichtung, Technik-Einbau, Endkontrolle: Sie sehen jeden Schritt mit Datum, animiert und verständlich."],
      ["Automatisch informiert", "Bei jedem Fortschritt bekommen Sie eine E-Mail. Sie müssen nichts nachfragen und nichts nachsehen."],
      ["Sendung verfolgen", "Voraussichtlicher Fertigstellungstermin, dann Sendungsnummer zum Kopieren und direkter Link zur Sendungsverfolgung."],
      ["Alles griffbereit", "Rechnung, Anleitungen und weitere Dokumente liegen jederzeit in Ihrem Kundenkonto zum Download."],
    ],
    cta: "Komplettsysteme ansehen", ctaTo: "/produkte", note: "Kundenkonto inklusive, ohne Aufpreis.",
    alt1: "Kundenkonto: Produktionsstatus einer Fotobox Premium mit animierter Werkstatt-Szene und Schrittliste",
    alt2: "Kundenkonto auf dem Handy: Zubehör unterwegs mit Sendungsnummer zum Kopieren und Link zur Sendungsverfolgung",
  },
  en: {
    badge: "After your order", title: ["You are not on your own ", "after you buy."],
    text: "Transparency matters to us. That is why we show you openly how far your order has come: from production and technical installation to the final inspection and the freight forwarder at your door. All in your customer account, on your phone and on your computer.",
    points: [
      ["Follow production live", "Cutting, assembly, powder coating, technical installation, final inspection: you see every step with its date, animated and easy to follow."],
      ["Informed automatically", "You receive an e-mail at every step. No need to ask, no need to check."],
      ["Track your shipment", "Estimated completion date, then the tracking number to copy and a direct link to the carrier's tracking."],
      ["Everything at hand", "Invoice, manuals and other documents are always available for download in your customer account."],
    ],
    cta: "See all models", ctaTo: "/en/photo-booths", note: "Customer account included at no extra cost.",
    alt1: "Customer account: production status of a Fotobox Premium with an animated workshop scene and list of steps",
    alt2: "Customer account on a phone: accessories on their way with tracking number and tracking link",
  },
  nl: {
    badge: "Na uw bestelling", title: ["Na de aankoop staat u ", "er niet alleen voor."],
    text: "Transparantie vinden wij belangrijk. Daarom laten wij u open zien hoe ver uw bestelling is: van productie en technische inbouw tot de eindcontrole en de expediteur voor uw deur. Alles in uw klantaccount, op uw telefoon en op uw computer.",
    points: [
      ["Productie live volgen", "Zagen, montage, poedercoating, technische inbouw, eindcontrole: u ziet elke stap met datum, geanimeerd en begrijpelijk."],
      ["Automatisch op de hoogte", "Bij elke stap ontvangt u een e-mail. U hoeft niets na te vragen en niets na te kijken."],
      ["Zending volgen", "Verwachte opleverdatum, daarna het trackingnummer om te kopiëren en een directe link naar de track & trace."],
      ["Alles bij de hand", "Factuur, handleidingen en andere documenten staan altijd klaar om te downloaden in uw klantaccount."],
    ],
    cta: "Alle modellen bekijken", ctaTo: "/nl/fotoboxen", note: "Klantaccount inbegrepen, zonder meerprijs.",
    alt1: "Klantaccount: productiestatus van een Fotobox Premium met geanimeerde werkplaats en stappenlijst",
    alt2: "Klantaccount op de telefoon: accessoires onderweg met trackingnummer en track & trace link",
  },
};

export default function OrderTrackingPromo({ compact = false, testid = "order-tracking-promo" }) {
  const T = TXT[useLang()] || TXT.de;
  const POINTS = T.points.map(([title, text], i) => ({ icon: ICONS[i], title, text }));
  return (
    <section className={`${compact ? "py-10 md:py-14" : "py-16 md:py-24"} bg-white border-t border-hair overflow-hidden`} data-testid={testid}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-[1fr,1.05fr] gap-10 lg:gap-16 items-center">
        <div>
          <div className="pill-badge">{T.badge}</div>
          <h2 className="font-heading text-3xl md:text-5xl text-ink mt-4 leading-[1.05]">
            {T.title[0]}<span className="font-serif-italic text-brand-yellow">{T.title[1]}</span>
          </h2>
          <p className="mt-4 text-ink-soft text-base md:text-lg leading-relaxed max-w-xl">{T.text}</p>
          <ul className="mt-7 grid sm:grid-cols-2 gap-4">
            {POINTS.map((p, i) => (
              <motion.li key={p.title} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ delay: i * 0.05, duration: 0.4 }}
                className="flex gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-yellow/15 flex items-center justify-center shrink-0"><p.icon className="w-5 h-5 text-brand-yellow" strokeWidth={2.2} /></div>
                <div><div className="font-heading text-base text-ink leading-tight">{p.title}</div><div className="mt-1 text-sm text-ink-soft leading-snug">{p.text}</div></div>
              </motion.li>
            ))}
          </ul>
          {!compact && (
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={T.ctaTo} className="btn-accent" data-testid={`${testid}-cta`}>{T.cta} <ArrowRight className="w-4 h-4" /></Link>
              <span className="self-center text-xs text-ink-muted">{T.note}</span>
            </div>
          )}
        </div>
        <div className="relative">
          <div className="absolute -inset-6 rounded-[32px] bg-brand-yellow/10 blur-2xl pointer-events-none" aria-hidden />
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}
            className="relative rounded-2xl overflow-hidden border border-hair shadow-2xl bg-white">
            <img {...imgSet("/images/kundenkonto/status-desktop.webp", "(max-width: 768px) 100vw, 50vw")} alt={T.alt1} loading="lazy" decoding="async" className="block w-full h-auto" width="1600" height="1113" />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: 0.15 }}
            className="relative sm:absolute sm:-bottom-8 sm:-right-4 sm:w-[46%] mt-4 sm:mt-0 rounded-2xl overflow-hidden border border-hair shadow-2xl bg-white">
            <img {...imgSet("/images/kundenkonto/versand-handy.webp", "(max-width: 768px) 100vw, 50vw")} alt={T.alt2} loading="lazy" decoding="async" className="block w-full h-auto" width="780" height="1790" style={{ maxHeight: 440, objectFit: "cover", objectPosition: "top" }} />
          </motion.div>
          <div className="hidden sm:block h-10" aria-hidden />
        </div>
      </div>
    </section>
  );
}
